'use client';

import { motion } from 'motion/react';
import Link from 'next/link';
import { useMemo, useState } from 'react';

import { ImageMedia } from '@/components/ImageMedia';
import { VideoMedia } from '@/components/VideoMedia';
import { useLocale } from '@/contexts/LocaleContext';
import type { Event, EventsTag, Media } from '@/payload-types';
import { cn } from '@/utils/cn';
import { getDictionary } from '@/utils/getDictionary';
import { pushGTMEvent } from '@/utils/pushGTMEvent';

import styles from './EventCard.module.scss';

type EventCardProps = Event & {
  className?: string;
  tracking?: {
    type: string;
    headline: string;
    cta: string;
  };
  tagsNoBackground?: boolean;
};

const invertedThemes = ['indigo-800', 'indigo-500', 'mint-800'];

export const EventCard: React.FC<EventCardProps> = (props) => {
  const {
    card,
    className,
    eventDetails,
    hero,
    tags,
    tagsNoBackground,
    title,
    tracking,
    url,
  } = props;
  const [motionComplete, setMotionComplete] = useState(false);
  const MotionLink = useMemo(() => motion.create(Link), []);
  const locale = useLocale();
  const t = getDictionary(locale);

  const {
    image: heroImage,
    mediaType: heroMediaType,
    videoEmbedUrl: heroVideoEmbedUrl,
    videoPoster: heroVideoPoster,
  } = hero;

  const {
    image: cardImage,
    mediaType: cardMediaType,
    override,
    title: cardTitle,
    videoEmbedUrl: cardVideoEmbedUrl,
    videoPoster: cardVideoPoster,
  } = card;

  const titleToUse = (override && cardTitle) || title;

  const startDate =
    (eventDetails?.dates?.startDate &&
      new Date(eventDetails.dates.startDate)) ||
    null;
  const endDate =
    (eventDetails?.dates?.endDate && new Date(eventDetails.dates.endDate)) ||
    null;
  const sameYear =
    startDate && startDate.getFullYear() === endDate?.getFullYear();
  const sameMonth = startDate && startDate.getMonth() === endDate?.getMonth();
  const sameDay = startDate && startDate.getDate() === endDate?.getDate();

  const MediaComponent =
    override && cardMediaType === 'image' && cardImage ? (
      <ImageMedia
        aspectRatio={'square'}
        image={cardImage}
        className={styles.media}
        responsiveWidths={{
          mobile: 343,
          desktop: 512,
          largeDesktop: 2256,
        }}
      />
    ) : override && cardMediaType === 'video' && cardVideoEmbedUrl ? (
      <VideoMedia
        aspectRatio={'square'}
        videoPoster={cardVideoPoster as Media}
        videoEmbedUrl={cardVideoEmbedUrl}
        videoIsBackground={true}
        videoLoops={true}
        className={styles.media}
        delayed={!motionComplete}
      />
    ) : heroMediaType === 'image' && heroImage ? (
      <ImageMedia
        aspectRatio={'square'}
        image={heroImage}
        className={styles.media}
        responsiveWidths={{
          mobile: 343,
          desktop: 512,
          largeDesktop: 2256,
        }}
      />
    ) : heroMediaType === 'video' && heroVideoEmbedUrl ? (
      <VideoMedia
        aspectRatio={'square'}
        videoPoster={heroVideoPoster as Media}
        videoEmbedUrl={heroVideoEmbedUrl}
        videoIsBackground={true}
        videoLoops={true}
        className={styles.media}
        delayed={!motionComplete}
      />
    ) : null;

  const handleClick = () => {
    if (tracking) {
      pushGTMEvent({
        event: 'component_click',
        component_cta: tracking.cta,
        component_headline: tracking.headline,
        component_type: tracking.type,
      });
    }
  };

  return (
    <MotionLink
      href={url as string}
      className={cn(styles.card, className)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      style={{
        willChange: 'opacity',
      }}
      layout='position'
      onAnimationComplete={() => setMotionComplete(true)}
      onClick={handleClick}
    >
      {MediaComponent}
      <div className={styles.content}>
        {titleToUse && (
          <h3 className={styles.title}>
            {titleToUse}

            {tags && (
              <div className={styles.tags}>
                {(tags as EventsTag[]).map((tag) => (
                  <span
                    key={tag.id}
                    className={cn(
                      styles.tag,
                      tagsNoBackground ? styles.tagNoBackground : '',
                      !tagsNoBackground && tag.backgroundColor
                        ? `${styles[`tag--${tag.backgroundColor}`]}`
                        : '',
                      !tagsNoBackground &&
                        invertedThemes.includes(
                          tag.backgroundColor as string
                        ) &&
                        styles.inverted
                    )}
                  >
                    {tag.title}
                  </span>
                ))}
              </div>
            )}
          </h3>
        )}
        {eventDetails.description && (
          <span className={styles.description}>{eventDetails.description}</span>
        )}
        <button
          className={cn(
            !tagsNoBackground
              ? 'button button--level-5 button--small'
              : 'button button--level-4 button--small'
          )}
        >
          {t('events.goToEvent')}
        </button>
      </div>
      <div className={styles.dateAndLocation}>
        {startDate && endDate && (
          <time dateTime={startDate.toISOString()} className={styles.date}>
            <span className={styles.year}>
              {sameYear
                ? endDate.toLocaleDateString('da-DK', { year: 'numeric' })
                : `${startDate.toLocaleDateString('da-DK', { year: 'numeric' })}-${endDate.toLocaleDateString('da-DK', { year: 'numeric' })}`}
            </span>
            <span className={styles.day}>
              {sameDay
                ? endDate.toLocaleDateString('da-DK', { day: 'numeric' })
                : `${startDate.toLocaleDateString('da-DK', { day: 'numeric' })}-${endDate.toLocaleDateString('da-DK', { day: 'numeric' })}`}
            </span>
            <span className={styles.month}>
              {sameMonth
                ? endDate.toLocaleDateString('da-DK', { month: 'long' })
                : `${startDate.toLocaleDateString('da-DK', { month: 'long' })}-${endDate.toLocaleDateString('da-DK', { month: 'long' })}`}
            </span>
          </time>
        )}
        <span className={styles.location}>
          {eventDetails.location?.online
            ? t('events.online')
            : eventDetails.location?.city
              ? eventDetails.location?.city
              : ''}
        </span>
      </div>
    </MotionLink>
  );
};
